import React, { useState, useMemo } from "react";
import { Modal, Paper, Button, Tooltip, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import "components/pages/transmissions/modal/process/ProcessModal.css";
import LoadingSpinner from "components/LoadingSpinner";
import { useSelector, useDispatch } from "react-redux";

import {
  changeSelectedRows,
  toggleModal,
} from "app/store/transmission/slice/transmissionSlice";
import {
  useProcessReportsMutation,
  useSignReportsMutation,
} from "app/store/transmission/api/transmissionTableApi";
import {
  showErrorToast,
  showSuccessToast,
  showToastWithUndo,
} from "utils/toast";
import {
  CLINIC_ACTIVE,
  CLINIC_PROCESSED,
  CLINIC_FOR_REVIEW,
} from "static/constants";
import SignProcessDataReviewModal from "./SignProcessDataReviewModal";

const SignProcessModal = ({ setRowSelection }) => {
  const {
    selectedTab,
    selectedReports,
    totalSelectedRows,
    isSignAndProcessModal,
    reportList,
  } = useSelector((state) => state.transmission);

  const dispatch = useDispatch();

  const [isSignAndProcessing, setIsSignAndProcessing] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(true);

  const [signReports] = useSignReportsMutation();
  const [processReports] = useProcessReportsMutation();

  const hasAnyNurseFlag = useMemo(() => {
    return selectedReports?.some((reportId) => {
      const report = reportList?.find((r) => r._id === reportId);
      return report?.nurse_flag;
    });
  }, [selectedReports, reportList]);

  const undoAction = async (removedReports) => {
    try {
      const newArr = selectedReports?.filter((report) =>
        removedReports?.includes(report)
      );

      const result = await processReports({
        reports: newArr,
        view:
          selectedTab === CLINIC_ACTIVE ? CLINIC_FOR_REVIEW : CLINIC_PROCESSED,
        processModalState: "unprocess",
      }).unwrap();

      if (result?.showError || result?.error) {
        await showErrorToast(result?.message);
      } else {
        showSuccessToast("Undo Successful");
      }
    } catch (err) {
      showErrorToast(err?.data?.message);
    }
  };

  const onConfirm = async (reportsToProcess = selectedReports) => {
    const reports = reportsToProcess || selectedReports;
    setIsSignAndProcessing(true);
    try {
      const response = await signReports(reports);
      const errorMessage = response?.error?.message;

      if (errorMessage) {
        showErrorToast(errorMessage);
        return;
      }

      const result = await processReports({
        reports: reports,
        view: selectedTab,
        processModalState: "process",
        toReturn: false,
      }).unwrap();

      const { data, showError, message } = result;

      let undoProcessAction = null;
      if (selectedTab === CLINIC_FOR_REVIEW) {
        undoProcessAction = async () => {
          await undoAction(data?.removedReports);
        };
      }

      if (showError) {
        await showToastWithUndo(result?.message, undoProcessAction, true);
        return;
      }

      if (data?.removedReports?.length > 0) {
        await showToastWithUndo(message, undoProcessAction);
        setRowSelection?.({});
      }
    } catch (e) {
      showErrorToast(e?.message);
    } finally {
      dispatch(toggleModal({ type: "signAndProcess", value: false }));
      dispatch(changeSelectedRows({}));
      setIsSignAndProcessing(false);
    }
  };

  const onClose = () => {
    setIsSignAndProcessing(false);
    dispatch(toggleModal({ type: "signAndProcess", value: false }));
    setShowReviewModal(true);
  };

  const handleReviewClose = () => {
    setShowReviewModal(true);
    dispatch(toggleModal({ type: "signAndProcess", value: false }));
  };

  const handleIgnoreAndContinue = () => {
    // Filter out all reports with nurse_flag
    const updatedReports = selectedReports?.filter((reportId) => {
      const report = reportList?.find((r) => r._id === reportId);
      return !report?.nurse_flag;
    });

    // Call onConfirm with filtered reports
    onConfirm(updatedReports);
  };

  const handleBackToReview = () => {
    setShowReviewModal(true);
  };

  const getSignProcessText = () => {
    if (totalSelectedRows === 1) {
      return "this record";
    } else if (totalSelectedRows > 1) {
      return `these ${totalSelectedRows} records`;
    }

    return "";
  };

  return (
    <>
      {/* Data Review Modal - Only show if any report has nurse_flag */}
      {hasAnyNurseFlag && (
        <SignProcessDataReviewModal
          show={isSignAndProcessModal && showReviewModal}
          onClose={handleReviewClose}
          onIgnoreAndContinue={handleIgnoreAndContinue}
          isProcessing={isSignAndProcessing}
        />
      )}

      {/* Confirmation Modal */}
      <Modal
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
        open={isSignAndProcessModal && (!hasAnyNurseFlag || !showReviewModal)}
        onClose={onClose}
      >
        <div className="process-modal__container">
          <Paper>
            <div className="process-modal__wrapper">
              <Tooltip title="Close" placement="bottom">
                <Typography
                  component="div"
                  onClick={onClose}
                  className="process-modal__close"
                >
                  <CloseIcon />
                </Typography>
              </Tooltip>
              <div className="process-modal__text">
                {isSignAndProcessing ? (
                  <div>
                    <div className="process-modal__processing">
                      Signing And Processing {totalSelectedRows}{" "}
                      {totalSelectedRows > 1 ? "reports" : "report"}.
                    </div>
                    <div className="process-modal__spinner">
                      <LoadingSpinner color="secondary" />
                    </div>
                  </div>
                ) : (
                  <div>
                    Are you sure you want to sign and process{" "}
                    {getSignProcessText()}?
                  </div>
                )}
              </div>
              <div className="process-modal__buttons">
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => onConfirm()}
                  disabled={isSignAndProcessing}
                >
                  Sign & Process
                </Button>
                {hasAnyNurseFlag && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleBackToReview}
                    disabled={isSignAndProcessing}
                  >
                    Back
                  </Button>
                )}
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={onClose}
                  disabled={isSignAndProcessing}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </Paper>
        </div>
      </Modal>
    </>
  );
};

export default SignProcessModal;
